import '@banno/jha-wc/src/jha-tab-item/jha-tab-item.js';
import '@banno/jha-wc/src/jha-tab/jha-tab.js';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TmBaseComponent } from '../../tm-base.component';

export type TmTabItem = {
    label: string;
    href?: string;
    disabled?: boolean;
    active?: boolean;
    onClick?: (tab: TmTabItem, evt: Event) => void;
};

export const jhaWcTabTagName = 'tm-tab';
/**
 * collapse-run-off
 * tab-group-visible
 */
@customElement(jhaWcTabTagName)
export class TmTab extends TmBaseComponent {
    @property({ type: Array })
    public tabs: TmTabItem[] = [];

    @property({ type: Boolean, reflect: true })
    public hideMenu = false;

    @property({ type: Boolean, reflect: true })
    public collapsedTabs = false;

    @property({ type: Number, reflect: true })
    public maxVisibleTabs = 5;

    /**
     * Adds a border to the tab component
     */
    @property({ type: Boolean, reflect: true })
    public bordered = false;

    protected render() {
        return html`<jha-tab class="jha-tab" ?bordered=${this.bordered}
            >${this.renderTabs()}</jha-tab
        >`;
    }

    protected updated() {
        const tabElement = this.renderRoot.querySelector('#jha-tab');

        if (tabElement) {
            tabElement.removeAttribute('hide-menu');
            tabElement.removeAttribute('collapsed-tabs');
            tabElement.removeAttribute('max-visible-tabs');

            if (this.hideMenu) {
                tabElement.setAttribute('hide-menu', '');
            }
            if (this.collapsedTabs) {
                tabElement.setAttribute('collapsed-tabs', '');
            }
            if (this.maxVisibleTabs) {
                tabElement.setAttribute('max-visible-tabs', this.maxVisibleTabs.toString());
            }
        }
    }

    private handleTabClick(tab: TmTabItem, evt: Event) {
        this.setActiveTab(evt);
        if (tab.onClick) {
            tab.onClick(tab, evt);
        }
    }

    private renderTabs() {
        return this.tabs.map(
            (tab: TmTabItem) =>
                html`<jha-tab-item
                    href=${tab.href}
                    @click=${(evt: Event) => this.handleTabClick(tab, evt)}
                    >${tab.label}</jha-tab-item
                >`
        );
    }

    private setActiveTab(evt: Event) {
        const tabs = this.shadowRoot?.querySelectorAll('jha-tab-item');
        if (tabs) {
            tabs.forEach(item => {
                if (item === evt.target) {
                    item.setAttribute('active', '');
                } else {
                    item.removeAttribute('active');
                }
            });
        }
    }

    public static get styles() {
        return css`
            jha-tab[bordered] {
                border-bottom: 1px solid
                    var(--jha-tab-border-color, var(--jha-border-color, #e4e7ea));
            }

            jha-tab[bordered] > jha-tab-item {
                border-bottom-width: 1px;
                border-bottom-color: transparent;
            }

            jha-tab[bordered] > jha-tab-item[active],
            jha-tab[bordered] > jha-tab-item[completed] {
                border-bottom: 1px solid var(--jha-tab-item-border-color, blue);
                border-bottom-color: var(--jha-tab-item-border-color, blue);
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [jhaWcTabTagName]: TmTab;
    }
}
